import { clsx } from 'clsx'
import NextLink from 'next/link'
import {
  useContext,
  type ComponentPropsWithRef,
  type MouseEventHandler,
  type ReactElement,
} from 'react'

import { composeCMSRelativeHref } from '@lib/utils/url'

import { PageContext } from '@contexts/page-context'

import { isInternalUrl } from '@components/ui-core/utils/url'

import styles from './link.module.scss'

/**
 * Props for Link components - extends all native anchor element properties.
 * Inherits all standard HTML anchor attributes including href, target, onClick, etc.
 */
export type LinkProps = ComponentPropsWithRef<'a'> & {
  disabled?: boolean
}

/**
 * Internal component for rendering external links using regular HTML anchor elements.
 *
 * This component handles external URLs that point to domains outside the application.
 * It provides special click handling that allows for custom onClick handlers while
 * still navigating to the external URL.
 *
 */
function ExternalLink(props: LinkProps): ReactElement {
  const { href, onClick, target, children, ...rest } = props

  const handleOnClick: MouseEventHandler<HTMLAnchorElement> = async e => {
    const shouldPreventDefault = onClick && href
    if (shouldPreventDefault) {
      e.preventDefault()
    }
    await onClick?.(e)
    if (shouldPreventDefault) {
      location.href = href
    }
  }

  return (
    <a href={href} target={target} onClick={handleOnClick} {...rest}>
      {children}
    </a>
  )
}

/**
 * Internal component for rendering internal links using Next.js Link component.
 *
 * This component handles internal URLs that point to pages within the application.
 * It uses Next.js Link for client-side navigation and processes the href through
 * the CMS relative href composer to handle preview mode and other CMS-specific logic.
 *
 */
function InternalLink(props: LinkProps): ReactElement {
  const { href, onClick, target, children, ...rest } = props
  const { isPreview } = useContext(PageContext)
  const processedHref = composeCMSRelativeHref(href!, isPreview)

  const handleInternalClick: MouseEventHandler<HTMLAnchorElement> = async e => {
    await onClick?.(e)
  }

  return (
    <NextLink href={processedHref} target={target} onClick={handleInternalClick} {...rest}>
      {children}
    </NextLink>
  )
}

/**
 * Smart Link component that automatically handles internal and external links.
 *
 * This component automatically determines whether a URL is internal or external
 * and renders the appropriate component:
 * - Internal links: Uses Next.js Link for client-side navigation
 * - External links: Uses regular HTML anchor elements
 *
 * Internal URLs are determined by the `isInternalUrl` utility function, which checks
 * for relative paths, hash fragments, and URLs matching the configured domain.
 *
 * @example
 * ```tsx
 * // Internal link - uses Next.js Link
 * <Link href="/about">About Us</Link>
 *
 * // External link - uses HTML anchor
 * <Link href="https://google.com" target="_blank">Google</Link>
 *
 * // Hash fragment - treated as internal
 * <Link href="#section">Jump to Section</Link>
 *
 * // With custom onClick
 * <Link href="/contact" onClick={handleClick}>Contact</Link>
 * ```
 */
export function Link(props: LinkProps): ReactElement {
  const { href, className, disabled, ...rest } = props

  const isInternal = isInternalUrl(href)

  const disabledProps = disabled
    ? {
        'aria-disabled': disabled,
        inert: disabled,
        tabIndex: -1,
      }
    : {}

  return isInternal ? (
    <InternalLink
      href={href}
      className={clsx(styles.link, disabled && styles.disabled, className)}
      {...disabledProps}
      {...rest}
    />
  ) : (
    <ExternalLink
      href={href}
      className={clsx(styles.link, disabled && styles.disabled, className)}
      {...disabledProps}
      {...rest}
    />
  )
}
